/**
 * 演示站布局 — /demo 独立深色皮肤（免登录，无侧边栏/顶部导航）。
 *
 * 深色作用域对齐 design/assets/theme.css `.demo`（L180-197）与
 * .demo-topbar/.demo-cta/.watermark：全部深色取值集中在下方 DEMO_COLORS，
 * 不在组件内散落硬编码 hex。
 */
import { Outlet } from "react-router-dom";
import { Box, Button, ThemeProvider, Typography, createTheme } from "@mui/material";
import { theme } from "../../theme/theme";
import Badge from "../../components/Badge";
import fhsaiLogo from "../../assets/fhsai-logo.png";

/** 深色色值集中定义（逐项注释对齐 theme.css .demo 作用域）。 */
const DEMO_COLORS = {
  bg: "#0B1020", // .demo background
  paper: "#151B2E", // .demo .card / .matrix / .alert background
  primary: "#6366F1", // .demo 主色（spark 线/品牌徽标）
  topbar: "#0E1428", // .demo-topbar background
  line: "#232B45", // .demo 边框线（card/matrix/table/alert border-color）
  inset: "#1A2138", // .demo .btn-ghost/.capsule/.table thead/hover 底
  inkStrong: "#F1F5F9", // .demo 标题/数值/强调文字
  inkBody: "#CBD5E1", // .demo 正文
  inkMuted: "#64748B", // .demo .crumb/.stat .label 辅助文字
  watermark: "rgba(148,163,184,.10)", // .watermark span color
} as const;

/** 演示站嵌套主题：在基线 theme 上覆盖 dark 作用域令牌。 */
const demoTheme = createTheme({
  ...theme,
  palette: {
    ...theme.palette,
    mode: "dark",
    primary: { ...theme.palette.primary, main: DEMO_COLORS.primary },
    background: { default: DEMO_COLORS.bg, paper: DEMO_COLORS.paper },
    divider: DEMO_COLORS.line,
    lineSoft: DEMO_COLORS.line,
    grey: { ...theme.palette.grey, 50: DEMO_COLORS.inset },
    brand: { ...theme.palette.brand, 50: DEMO_COLORS.inset },
    semanticBg: { ...theme.palette.semanticBg, neutral: DEMO_COLORS.line },
    text: {
      primary: DEMO_COLORS.inkStrong,
      secondary: DEMO_COLORS.inkBody,
      disabled: DEMO_COLORS.inkMuted,
    },
  },
  components: {
    ...theme.components,
    MuiCard: {
      styleOverrides: {
        // 对齐 .demo .card：深色下去投影、边框取 .demo 线色
        root: ({ theme: t }: { theme: import("@mui/material/styles").Theme }) => ({
          border: `1px solid ${t.palette.divider}`,
          borderRadius: 12,
          boxShadow: "none",
        }),
      },
    },
  },
});

/** 平滑锚跳到留资区。 */
export function scrollToLeadForm() {
  document.getElementById("lead-form")?.scrollIntoView({ behavior: "smooth" });
}

/** CTA 渐变（对齐 .demo-cta：linear-gradient(90deg,#4F46E5,#7C3AED)）。 */
export const DEMO_CTA_SX = {
  background: "linear-gradient(90deg,#4F46E5,#7C3AED)",
  color: "#fff",
  "&:hover": { background: "linear-gradient(90deg,#4F46E5,#7C3AED)", opacity: 0.9 },
} as const;

export default function DemoLayout() {
  return (
    <ThemeProvider theme={demoTheme}>
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
        {/* 全屏斜水印「示例数据」（对齐 .watermark：固定定位、低透明度、
            pointer-events none；置顶层位于所有 demo 区块之上可见） */}
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
          }}
        >
          <Box
            component="span"
            sx={{
              fontSize: 64,
              fontWeight: 800,
              color: DEMO_COLORS.watermark,
              transform: "rotate(-18deg)",
              letterSpacing: ".2em",
              whiteSpace: "nowrap",
            }}
          >
            示例数据
          </Box>
        </Box>

        {/* 演示站顶条（对齐 .demo-topbar：sticky、无侧边栏） */}
        <Box
          component="header"
          sx={{
            height: 56,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 3,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: DEMO_COLORS.topbar,
            position: "sticky",
            top: 0,
            zIndex: 100,
          }}
        >
          <Box
            component="img"
            src={fhsaiLogo}
            alt="FHSAI"
            sx={{ height: 24 }}
          />
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: "text.primary" }}>
            FHSAI · 产品体验
          </Typography>
          <Box sx={{ ml: "auto", display: "flex", gap: 1.5, alignItems: "center" }}>
            <Badge label="示例数据环境" variant="default" />
            <Button size="small" onClick={scrollToLeadForm} sx={DEMO_CTA_SX}>
              预约真实演示
            </Button>
          </Box>
        </Box>

        {/* 页面内容（水印之下可交互） */}
        <Box sx={{ position: "relative", zIndex: 1, maxWidth: 1200, mx: "auto", px: 3, pb: 4 }}>
          <Outlet />
        </Box>
      </Box>
    </ThemeProvider>
  );
}
